// Reubicación interna — Inventario → Operaciones → Reubicación.
// Mueve cantidades entre bins de la MISMA bodega (RPC inventario.inv_reubicar).
// No cambia el total ni el costo de la bodega; solo la ubicación física.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { BuscadorProducto, Campo, Location, ProductoOpt, UbicacionOpt, fieldCls, inv, invRpc, invalidateInventario, locationsList, productosOptList, ubicacionesList } from './shared';

interface Linea { producto_id: string; origen: string; destino: string; cantidad: string }
interface StockUbic { producto_id: string; ubicacion_id: string; unidades: number }
const nuevaLinea = (): Linea => ({ producto_id: '', origen: '', destino: '', cantidad: '' });

export function Reubicaciones() {
  const qc = useQueryClient();
  const [bodega, setBodega] = useState('');
  const [lineas, setLineas] = useState<Linea[]>([nuevaLinea()]);
  const [obs, setObs] = useState('');

  const { data: bodegas = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList });
  const { data: productos = [] } = useQuery<ProductoOpt[]>({ queryKey: ['inv-productos-opt'], queryFn: productosOptList });
  const { data: bins = [] } = useQuery<UbicacionOpt[]>({ queryKey: ['inv-ubic', bodega], queryFn: () => ubicacionesList(bodega), enabled: !!bodega });
  const { data: stock = [] } = useQuery<StockUbic[]>({
    queryKey: ['inv-stock-ubic', bodega],
    queryFn: () => inv.list<StockUbic>('stockcurrent_ubic', `?location_id=eq.${bodega}&select=producto_id,ubicacion_id,unidades`),
    enabled: !!bodega,
  });
  const dispo = useMemo(() => { const m = new Map<string, number>(); for (const s of stock) m.set(`${s.producto_id}|${s.ubicacion_id}`, Number(s.unidades)); return m; }, [stock]);

  const setLinea = (i: number, patch: Partial<Linea>) => setLineas((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const addLinea = () => setLineas((ls) => [...ls, nuevaLinea()]);
  const delLinea = (i: number) => setLineas((ls) => ls.filter((_, j) => j !== i));

  const validas = lineas.filter((l) => l.producto_id && l.origen && l.destino && l.origen !== l.destino && Number(l.cantidad) > 0);

  const confirmar = useMutation({
    // La RPC recibe UN solo argumento llamado `payload`. PostgREST resuelve la
    // función por los nombres de los argumentos: mandar los campos sueltos da
    // 404 «Could not find the function», no un error de validación.
    mutationFn: () => invRpc<{ movimientos: number }>('inv_reubicar', {
      payload: {
        location_id: bodega, observaciones: obs || null,
        lineas: validas.map((l) => ({ producto_id: l.producto_id, ubicacion_origen_id: l.origen, ubicacion_destino_id: l.destino, cantidad: Number(l.cantidad) })),
      },
    }),
    onSuccess: (r) => {
      toast.success(`${r.movimientos} reubicación(es) registradas`);
      setLineas([nuevaLinea()]); setObs('');
      invalidateInventario(qc); void qc.invalidateQueries({ queryKey: ['inv-stock-ubic', bodega] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <ArrowLeftRight className="w-7 h-7 text-primary" />
        <div>
          <h1 className="text-xl font-semibold">Reubicación interna</h1>
          <p className="text-sm text-muted-foreground">Mover mercadería entre ubicaciones de la misma bodega (no altera costo ni total)</p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-4 flex flex-wrap items-end gap-3">
          <Campo label="Bodega" className="min-w-64">
            <select className={fieldCls} value={bodega} onChange={(e) => { setBodega(e.target.value); setLineas([nuevaLinea()]); }}>
              <option value="">— Selecciona una bodega —</option>
              {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Observaciones" className="flex-1 min-w-64"><input className={fieldCls} value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Opcional" /></Campo>
        </CardContent>
      </Card>

      {!bodega ? (
        <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">Elige una bodega para reubicar su mercadería.</CardContent></Card>
      ) : (
        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base">Movimientos</CardTitle>
            <Button size="sm" variant="outline" onClick={addLinea}><Plus className="w-4 h-4 mr-1" /> Agregar línea</Button>
          </CardHeader>
          <CardContent className="overflow-x-auto space-y-3">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-2">Producto</th><th className="py-2 pr-2">Ubic. origen</th><th className="py-2 pr-2 text-right">Disp.</th>
                <th className="py-2 pr-2">Ubic. destino</th><th className="py-2 pr-2 text-right">Cantidad</th><th className="py-2"></th>
              </tr></thead>
              <tbody>
                {lineas.map((l, i) => {
                  const disp = l.producto_id && l.origen ? (dispo.get(`${l.producto_id}|${l.origen}`) ?? 0) : null;
                  return (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-1 pr-2 min-w-56">
                        <BuscadorProducto productos={productos} value={l.producto_id} compact
                          onChange={(id) => setLinea(i, { producto_id: id })} />
                      </td>
                      <td className="py-1 pr-2">
                        <select className={fieldCls} value={l.origen} onChange={(e) => setLinea(i, { origen: e.target.value })}>
                          <option value="">— Origen —</option>
                          {bins.map((u) => <option key={u.id} value={u.id}>{u.codigo}</option>)}
                        </select>
                      </td>
                      <td className="py-1 pr-2 text-right tabular-nums text-muted-foreground">{disp === null ? '—' : disp}</td>
                      <td className="py-1 pr-2">
                        <select className={fieldCls} value={l.destino} onChange={(e) => setLinea(i, { destino: e.target.value })}>
                          <option value="">— Destino —</option>
                          {bins.filter((u) => u.id !== l.origen).map((u) => <option key={u.id} value={u.id}>{u.codigo}</option>)}
                        </select>
                      </td>
                      <td className="py-1 pr-2 w-28"><input type="number" min="0" step="0.01" className={`${fieldCls} text-right`} value={l.cantidad} onChange={(e) => setLinea(i, { cantidad: e.target.value })} /></td>
                      <td className="py-1 text-right">{lineas.length > 1 && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title="Quitar" onClick={() => delLinea(i)}><Trash2 className="w-4 h-4" /></button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex items-center justify-between border-t pt-3">
              <span className="text-xs text-muted-foreground">{validas.length} línea(s) válida(s)</span>
              <Button isLoading={confirmar.isPending} disabled={validas.length === 0} onClick={() => confirmar.mutate()}>
                {!confirmar.isPending && <ArrowLeftRight className="w-4 h-4 mr-1" />} Reubicar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
